import type { StampDutyRuleSet } from "./types.js";

// QLD transfer duty — current rates (1 May 2025 new-build concession era).
//
// General transfer-duty schedule transcribed VERBATIM from QRO "Transfer duty
// rates" (page last updated 12 March 2026, fetched 2026-06-22 —
// stamp-duty-calculator-figures.md Fig 1):
//
//   $0          – $5,000      : nil
//   $5,001      – $75,000     : $1.50 per $100 over $5,000
//   $75,001     – $540,000    : $1,050   + $3.50 per $100 over $75,000
//   $540,001    – $1,000,000  : $17,325  + $4.50 per $100 over $540,000
//   $1,000,001  and over      : $38,025  + $5.75 per $100 over $1,000,000
//
// Foreign acquirer additional duty (AFAD): 8% on residential land (Fig 7,
// CITED).
//
// FIRST-HOME CONCESSIONS (first-home ledger Figs 3 & 4, both CITED) — the
// load-bearing QLD branch:
//
//  - ESTABLISHED home first-home concession: full relief (nil duty) at/below
//    $700,000, sliding to $800,000, max duty saving $24,525, NO concession at/
//    above $800,000. (Threshold raised $500k→$700k for agreements on/after
//    9 Jun 2024.) Note $24,525 == the full general duty at exactly $700,000
//    (so the concession reduces a $700k purchase to nil), phasing to $0 by
//    $800k — modelled as `qld-saving` with maxSaving $24,525.
//
//  - NEW (or substantially-renovated, never-occupied) home first-home
//    concession: from 1 May 2025, FULL concession reducing duty to NIL with NO
//    VALUE CAP — regardless of price. The $700k threshold above is the
//    ESTABLISHED-home figure and does NOT cap the new-build concession. Buyer
//    must pay market value. This is `firstHomeNewBuild` and is checked FIRST in
//    the first-home calc.
//
// FHOG context (not a duty figure, so not encoded as calc logic): QLD also runs
// a First Home Owner Grant for eligible NEW builds — a separate cash grant from
// QRO, distinct from this duty concession. We do not bake a grant amount into
// duty (it is not part of the duty payable); copy may mention it as a separate
// cited fact.

export const qld2025_26: StampDutyRuleSet = {
  state: "QLD",
  period: "2025-26",
  effectiveFrom: "2025-05-01",
  effectiveTo: null,
  sources: {
    transferDuty: "https://qro.qld.gov.au/duties/transfer-duty/calculate/rates/",
    foreignSurcharge: "https://qro.qld.gov.au/duties/transfer-duty/calculate/rates/",
    firstHomeEstablished:
      "https://qro.qld.gov.au/duties/transfer-duty/concessions/homes/first-home/",
    firstHomeNew:
      "https://qro.qld.gov.au/duties/transfer-duty/concessions/homes/first-home-new-home/",
  },
  sourcesVerifiedAt: "2026-06-22",
  brackets: [
    { from: 0, to: 5_001, base: 0, rate: 0, over: 0 },
    { from: 5_001, to: 75_001, base: 0, rate: 0.015, over: 5_000 },
    { from: 75_001, to: 540_001, base: 1_050, rate: 0.035, over: 75_000 },
    { from: 540_001, to: 1_000_001, base: 17_325, rate: 0.045, over: 540_000 },
    { from: 1_000_001, to: null, base: 38_025, rate: 0.0575, over: 1_000_000 },
  ],
  foreignSurchargeRate: 0.08,
  foreignSurchargeFrom: "2018-07-01",
  firstHome: {
    fullExemptionUpTo: 700_000,
    taperEndsAt: 800_000,
    taper: "qld-saving",
    maxSaving: 24_525,
  },
  firstHomeNewBuild: {
    from: "2025-05-01",
    uncapped: true,
  },
};
